import { Sheet, SheetContent, SheetTitle } from '@avala/ui/components/primitives/sheet';
import { Button, Input } from '../components/admin/ui';
import { useEffect, useRef, useState } from 'react';
import { Building2, Settings } from 'lucide-react';
import { adminApi, Organization } from '../lib/admin-api';
import { DataTable } from '../components/admin/DataTable';

export function OrganizationsPage() {
  const editTrigger = useRef<HTMLButtonElement | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      const data = await adminApi.getOrganizations();
      setOrganizations(data);
      setLoading(false);
    }

    fetchData();
  }, []);

  const getPlanBadge = (plan: string) => {
    const styles: Record<string, string> = {
      free: 'bg-muted text-foreground',
      pro: 'bg-info text-info-foreground',
      enterprise: 'bg-info text-info-foreground',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[plan] || styles.free}`}>
        {plan}
      </span>
    );
  };

  const columns = [
    {
      key: 'name',
      header: 'Organization',
      render: (org: Organization) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/15 rounded-lg flex items-center justify-center">
            <Building2 className="w-5 h-5 text-link" />
          </div>
          <div>
            <p className="font-medium text-foreground">{org.name}</p>
            <p className="text-sm text-muted-foreground">{org.slug}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'plan',
      header: 'Plan',
      render: (org: Organization) => getPlanBadge(org.plan),
    },
    {
      key: 'settings',
      header: 'AI Enabled',
      render: (org: Organization) => {
        const aiEnabled = (org.settings as { features?: { ai_enabled?: boolean } })?.features?.ai_enabled;
        return (
          <span className={aiEnabled ? 'text-success-foreground' : 'text-muted-foreground'}>
            {aiEnabled ? 'Yes' : 'No'}
          </span>
        );
      },
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (org: Organization) => (
        <span className="text-muted-foreground">
          {new Date(org.created_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (org: Organization) => (
        <Button aria-label="Edit item" size="iconSm" variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            editTrigger.current = e.currentTarget;
            setEditingOrg(org);
          }}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
          <Settings className="w-4 h-4 text-muted-foreground" />
        </Button>
      ),
    },
  ];

  return (
    <Sheet open={editingOrg !== null} onOpenChange={open => { if (!open) setEditingOrg(null); }}>
    <div className="support-page">
      <div className="support-page-heading">
        <div>
          <h1 className="support-page-title">Organizations</h1>
          <p className="text-muted-foreground mt-1">Manage organizations and their settings</p>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={organizations}
        loading={loading}
        emptyMessage="No organizations found"
        keyExtractor={(org) => org.id}
      />

      {/* Edit Modal */}
      {editingOrg && (
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg" aria-describedby={undefined} onCloseAutoFocus={event => { event.preventDefault(); editTrigger.current?.focus(); }}>
            <SheetTitle className="text-xl font-medium text-foreground mb-6">
              Edit organization
            </SheetTitle>

            <div className="space-y-4">
              <div>
                <label htmlFor="organizationspage-1" className="block text-sm font-medium text-foreground mb-1">
                  Name
                </label>
                <Input id="organizationspage-1"
                  type="text"
                  value={editingOrg.name}
                  onChange={(e) =>
                    setEditingOrg({ ...editingOrg, name: e.target.value })
                  }
                  className="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-primary"
                />
              </div>

              <div>
                <label htmlFor="organizationspage-2" className="block text-sm font-medium text-foreground mb-1">
                  Slug
                </label>
                <Input id="organizationspage-2"
                  type="text"
                  value={editingOrg.slug}
                  disabled
                  className="w-full border border-border rounded-md px-3 py-2 bg-muted text-muted-foreground"
                />
              </div>

              <div>
                <label htmlFor="organizationspage-3" className="block text-sm font-medium text-foreground mb-1">
                  Plan
                </label>
                <select id="organizationspage-3"
                  value={editingOrg.plan}
                  onChange={(e) =>
                    setEditingOrg({ ...editingOrg, plan: e.target.value })
                  }
                  className="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-primary"
                >
                  <option value="free">Free</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="ghost"
                onClick={() => setEditingOrg(null)}
                className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
              >
                Cancel
              </Button>
              <Button variant="ghost"
                onClick={async () => {
                  const success = await adminApi.updateOrganization(
                    editingOrg.id,
                    { name: editingOrg.name, plan: editingOrg.plan }
                  );
                  if (success) {
                    setOrganizations((prev) =>
                      prev.map((o) => (o.id === editingOrg.id ? editingOrg : o))
                    );
                  }
                  setEditingOrg(null);
                }}
                className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary rounded-lg transition-colors"
              >
                Save Changes
              </Button>
            </div>
        </SheetContent>
      )}
    </div>
    </Sheet>
  );
}
